import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MessageSquareLock, ArrowLeft, Mail, Phone, Check } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { useCart } from '@/store/cart.store';
import { sendCode, verifyCode, sendEmailCode, verifyEmailCode } from '@/services/client';

type Mode = 'phone' | 'email';
type Step = 'input' | 'code';

export default function Login() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  // Клиент регистрируется в рамках конкретного заведения
  // (берём из cart store — туда id кладётся при заходе на меню).
  const establishmentId = useCart((s) => s.establishmentId);

  // По умолчанию основной вход — по email; телефон доступен как альтернатива.
  const [mode, setMode] = useState<Mode>('email');
  const [step, setStep] = useState<Step>('input');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  // 152-ФЗ: согласие должно быть явным и активным действием пользователя.
  // Без отметки ни форма отправки, ни верификация не доступны.
  const [consent, setConsent] = useState(false);
  // 38-ФЗ «О рекламе»: согласие на информационные/рекламные рассылки —
  // отдельное, добровольное, не блокирует регистрацию.
  const [marketing, setMarketing] = useState(false);

  const ensureConsent = (): boolean => {
    if (!consent) {
      toast.error('Необходимо согласие на обработку персональных данных');
      return false;
    }
    return true;
  };

  const sendPhone = async () => {
    if (!ensureConsent()) return;
    if (phone.replace(/\D/g, '').length < 10) return toast.error('Введите телефон');
    setLoading(true);
    try {
      await sendCode(phone, establishmentId);
      setStep('code');
      toast.success('Код отправлен');
    } catch { /* api показывает ошибку */ }
    finally { setLoading(false); }
  };

  const sendEmail = async () => {
    if (!ensureConsent()) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error('Введите корректный email');
    setLoading(true);
    try {
      await sendEmailCode(email, establishmentId);
      setStep('code');
      toast.success('Код отправлен на почту');
    } catch { /* api показывает ошибку */ }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (code.length < 4) return toast.error('Неверный код');
    setLoading(true);
    try {
      const res =
        mode === 'phone'
          ? await verifyCode(phone, code, name || undefined, establishmentId)
          : await verifyEmailCode(email, code, name || undefined, establishmentId);
      setSession({ accessToken: res.access_token, refreshToken: res.refresh_token, user: res.user });
      toast.success('Вы вошли');
      navigate(-1);
    } catch { /* api показывает ошибку */ }
    finally { setLoading(false); }
  };

  const resetFlow = () => {
    setStep('input');
    setCode('');
    setName('');
  };

  return (
    <div className="min-h-full flex flex-col px-6 pt-6 pb-10 max-w-sm mx-auto">
      <button
        className="text-slate-500 text-sm flex items-center gap-1 self-start -ml-1"
        onClick={() => (step === 'code' ? resetFlow() : navigate(-1))}
      >
        <ArrowLeft size={16} /> Назад
      </button>

      <div className="flex-1 flex flex-col justify-center">
        <div className="w-14 h-14 rounded-2xl bg-brand-600 text-white flex items-center justify-center mb-5">
          <MessageSquareLock size={24} />
        </div>

        <h1 className="text-3xl font-semibold leading-tight">
          {step === 'code'
            ? 'Введите код'
            : mode === 'phone'
              ? 'Вход по номеру'
              : 'Вход по Email'}
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          {step === 'code'
            ? `Отправили код на ${mode === 'phone' ? phone : email}`
            : mode === 'phone'
              ? 'Отправим SMS-код для подтверждения'
              : 'Отправим код на почту'}
        </p>

        {step === 'input' && mode === 'phone' && (
          <div className="mt-7 space-y-3">
            <input
              className="input"
              placeholder="+7 (999) 123-45-67"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              autoFocus
            />
            <button
              className="btn-primary w-full"
              onClick={sendPhone}
              disabled={loading || !consent}
            >
              {loading ? 'Отправляем…' : 'Получить код'}
            </button>

            <ConsentBlock
              consent={consent}
              onConsentChange={setConsent}
              marketing={marketing}
              onMarketingChange={setMarketing}
            />

            <DividerOr />

            <button
              className="btn-outline w-full"
              onClick={() => setMode('email')}
              type="button"
            >
              <Mail size={16} /> Войти по Email
            </button>
          </div>
        )}

        {step === 'input' && mode === 'email' && (
          <div className="mt-7 space-y-3">
            <input
              className="input"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputMode="email"
              autoComplete="email"
              autoFocus
            />
            <button
              className="btn-primary w-full"
              onClick={sendEmail}
              disabled={loading || !consent}
            >
              {loading ? 'Отправляем…' : 'Получить код'}
            </button>

            <ConsentBlock
              consent={consent}
              onConsentChange={setConsent}
              marketing={marketing}
              onMarketingChange={setMarketing}
            />

            <DividerOr />

            <button
              className="btn-outline w-full"
              onClick={() => setMode('phone')}
              type="button"
            >
              <Phone size={16} /> Войти по номеру
            </button>
          </div>
        )}

        {step === 'code' && (
          <div className="mt-7 space-y-3">
            {import.meta.env.DEV && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">
                Dev-режим: используйте код <span className="font-semibold">1234</span>
              </div>
            )}
            <input
              className="input tracking-[0.5em] text-center text-xl font-semibold"
              placeholder="••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoFocus
            />
            <input
              className="input"
              placeholder="Ваше имя (опционально)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <button className="btn-primary w-full" onClick={verifyOtp} disabled={loading}>
              {loading ? 'Проверяем…' : 'Войти'}
            </button>
            <button className="btn-ghost w-full text-sm" onClick={resetFlow}>
              Изменить {mode === 'phone' ? 'номер' : 'email'}
            </button>
          </div>
        )}

        <p className="text-[11px] text-slate-400 text-center mt-8 leading-relaxed">
          Обработка данных осуществляется в соответствии с Федеральным законом
          <br />от 27.07.2006 № 152-ФЗ «О персональных данных».
        </p>
      </div>
    </div>
  );
}

function DividerOr() {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-1 h-px bg-slate-200" />
      <span className="text-xs uppercase tracking-wider text-slate-400">или</span>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

// Блок согласий по аналогии с законопослушными формами регистрации в РФ:
// 1) обязательное — 152-ФЗ «О персональных данных» (без отметки регистрация недоступна);
// 2) добровольное — 38-ФЗ «О рекламе» (информационные/рекламные рассылки).
function ConsentBlock({
  consent,
  onConsentChange,
  marketing,
  onMarketingChange,
}: {
  consent: boolean;
  onConsentChange: (value: boolean) => void;
  marketing: boolean;
  onMarketingChange: (value: boolean) => void;
}) {
  return (
    <div className="pt-2 space-y-3">
      <ConsentRow checked={consent} onChange={onConsentChange}>
        Я ознакомлен(а) с{' '}
        <Link to="/legal/privacy" className="text-brand-600">политикой</Link>,{' '}
        <Link to="/legal/terms" className="text-brand-600">офертой</Link>{' '}
        и даю <span className="text-brand-600">согласие</span> на обработку персональных данных
      </ConsentRow>

      <ConsentRow checked={marketing} onChange={onMarketingChange}>
        Я даю <span className="text-brand-600">согласие</span> на получение информационных рассылок
      </ConsentRow>
    </div>
  );
}

// Квадратный чекбокс с белой галочкой внутри бренд-квадрата — как в примере.
function ConsentRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <span className="relative mt-0.5 flex-shrink-0">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          className={[
            'w-6 h-6 rounded-lg border flex items-center justify-center transition-colors',
            checked
              ? 'bg-brand-600 border-brand-600'
              : 'bg-white border-slate-300 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-400',
          ].join(' ')}
        >
          {checked && <Check size={16} strokeWidth={3} className="text-white" />}
        </span>
      </span>
      <span className="text-[13px] leading-snug text-slate-700">{children}</span>
    </label>
  );
}
